import { useLayoutEffect } from 'react';

import { css } from '@/styled-system/css';
import {
  DESKTOP_HEADER_HEIGHT_REM,
  SRP_CONTENT_PANEL_MAX_WIDTH,
  SRP_LAYOUT_SPACING_REM,
} from '@/src/layouts/persistentSearchLayout.constants';
import { MIN_LG } from '@/src/lib/breakpoints';
import { serializeForInlineScript } from '@/src/lib/utils/serializeForInlineScript';
import { STATIC_MAP_CAMERA_VERSION, type StaticMapBounds } from '@/src/lib/utils/staticMap';

const STATIC_MAP_PREVIEW_PROPERTY = '--hotpads-static-map-preview-image';
const STATIC_MAP_PREVIEW_LOADED_DATASET_KEY = 'hotpadsStaticMapPreviewLoaded';
const STATIC_MAP_PREVIEW_ERROR_DATASET_KEY = 'hotpadsStaticMapPreviewError';
const STATIC_MAP_PREVIEW_URL_DATASET_KEY = 'hotpadsStaticMapPreviewUrl';
const STATIC_MAP_HEAD_BOOTSTRAP_DATASET_KEY = 'hotpadsStaticMapBootstrap';
const STATIC_MAP_PREVIEW_LOAD_EVENT = 'hotpads:static-map-preview-load';
const STATIC_MAP_PREVIEW_ERROR_EVENT = 'hotpads:static-map-preview-error';
const STATIC_MAP_PREVIEW_REQUEST_TIMEOUT_MS = 2500;
const STATIC_MAP_PREVIEW_PATH = '/api/static-map';
const PREFERS_DARK = '(prefers-color-scheme: dark)';

const previewImage = css({
  width: '100%',
  height: '100%',
  display: 'block',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
});

function buildBoundsParams(bounds: StaticMapBounds): URLSearchParams {
  return new URLSearchParams({
    cameraVersion: STATIC_MAP_CAMERA_VERSION,
    minLat: String(bounds.minLat),
    maxLat: String(bounds.maxLat),
    minLon: String(bounds.minLon),
    maxLon: String(bounds.maxLon),
  });
}

function getStaticMapPreviewRequestPrefix(bounds: StaticMapBounds): string {
  return `${STATIC_MAP_PREVIEW_PATH}?${buildBoundsParams(bounds).toString()}&width=`;
}

function buildStaticMapImageRequestScript(bounds: StaticMapBounds): string {
  // The script assembles the URL from a params object: a complete URL literal in the HTML gets
  // extracted and fetched by crawlers, which took a 400 on the dimensionless prefix (HP-8950).
  // colorScheme follows width and height, so the request still starts with the owned prefix.
  const boundsEntries = serializeForInlineScript(Object.fromEntries(buildBoundsParams(bounds)));
  return `if(!Number.isFinite(w)||!Number.isFinite(h)||w<=0||h<=0)return;const u=${serializeForInlineScript(STATIC_MAP_PREVIEW_PATH)}+'?'+new URLSearchParams(Object.assign(${boundsEntries},{width:w,height:h},window.matchMedia(${serializeForInlineScript(PREFERS_DARK)}).matches?{colorScheme:'dark'}:{}));if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}===u)return;d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}=u;delete d.dataset.${STATIC_MAP_PREVIEW_LOADED_DATASET_KEY};delete d.dataset.${STATIC_MAP_PREVIEW_ERROR_DATASET_KEY};const i=new Image();i.fetchPriority='high';i.onload=()=>{if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}!==u)return;d.dataset.${STATIC_MAP_PREVIEW_LOADED_DATASET_KEY}='true';window.dispatchEvent(new Event(${serializeForInlineScript(STATIC_MAP_PREVIEW_LOAD_EVENT)}))};i.onerror=()=>{if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY}!==u)return;d.dataset.${STATIC_MAP_PREVIEW_ERROR_DATASET_KEY}='true';window.dispatchEvent(new Event(${serializeForInlineScript(STATIC_MAP_PREVIEW_ERROR_EVENT)}))};i.src=u;d.style.setProperty(${serializeForInlineScript(STATIC_MAP_PREVIEW_PROPERTY)},'url("'+u+'")')`;
}

export function buildStaticMapPreviewHeadBootstrapScript(bounds: StaticMapBounds): string {
  const requestScript = buildStaticMapImageRequestScript(bounds);
  // Layout values are inlined so this can run before the stylesheet loads; the gutter is reserved
  // only while measuring so clientWidth matches the stylesheet's `scrollbar-gutter: stable`.
  return `(()=>{if(!window.matchMedia(${serializeForInlineScript(MIN_LG)}).matches)return;const d=document.documentElement;if(d.dataset.${STATIC_MAP_PREVIEW_URL_DATASET_KEY})return;d.dataset.${STATIC_MAP_HEAD_BOOTSTRAP_DATASET_KEY}='true';d.style.scrollbarGutter='stable';const f=parseFloat(getComputedStyle(d).fontSize)||16,s=${SRP_LAYOUT_SPACING_REM}*f,t=${DESKTOP_HEADER_HEIGHT_REM}*f,a=d.clientWidth-s*2,w=Math.round(a-s-Math.min(a/2,${parseFloat(SRP_CONTENT_PANEL_MAX_WIDTH)})),h=Math.round(d.clientHeight-t-s);d.style.removeProperty('scrollbar-gutter');${requestScript}})()`;
}

export function buildStaticMapPreviewBootstrapScript(bounds: StaticMapBounds): string {
  const requestScript = buildStaticMapImageRequestScript(bounds);
  return `(()=>{const s=document.currentScript,m=s&&s.closest('#srp-map-panel');if(!m||!window.matchMedia(${serializeForInlineScript(MIN_LG)}).matches)return;const d=document.documentElement,r=m.getBoundingClientRect(),w=Math.round(r.width),h=Math.round(r.height);${requestScript}})()`;
}

function clearStaticMapPreviewState(): void {
  const root = document.documentElement;
  root.style.removeProperty(STATIC_MAP_PREVIEW_PROPERTY);
  delete root.dataset[STATIC_MAP_PREVIEW_LOADED_DATASET_KEY];
  delete root.dataset[STATIC_MAP_PREVIEW_ERROR_DATASET_KEY];
  delete root.dataset[STATIC_MAP_PREVIEW_URL_DATASET_KEY];
  delete root.dataset[STATIC_MAP_HEAD_BOOTSTRAP_DATASET_KEY];
}

/** Static snapshot started before hydration and kept aligned to the measured map panel. */
export function StaticMapPreview({
  bounds,
  onLoad,
  onError,
}: {
  bounds: StaticMapBounds;
  onLoad?: () => void;
  onError?: () => void;
}) {
  const requestPrefix = getStaticMapPreviewRequestPrefix(bounds);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const currentRequestUrl = root.dataset[STATIC_MAP_PREVIEW_URL_DATASET_KEY];
    const ownedRequestUrl = currentRequestUrl?.startsWith(requestPrefix) === true ? currentRequestUrl : undefined;
    const ownsCurrentRequest = () =>
      ownedRequestUrl !== undefined && root.dataset[STATIC_MAP_PREVIEW_URL_DATASET_KEY] === ownedRequestUrl;
    let timeoutHandle: number | undefined;
    const clearTimeoutHandle = () => {
      if (timeoutHandle === undefined) return;
      window.clearTimeout(timeoutHandle);
      timeoutHandle = undefined;
    };
    const handleLoad = () => {
      if (!ownsCurrentRequest()) return;
      clearTimeoutHandle();
      onLoad?.();
    };
    const handleError = () => {
      if (!ownsCurrentRequest()) return;
      clearTimeoutHandle();
      clearStaticMapPreviewState();
      onError?.();
    };

    window.addEventListener(STATIC_MAP_PREVIEW_LOAD_EVENT, handleLoad);
    window.addEventListener(STATIC_MAP_PREVIEW_ERROR_EVENT, handleError);
    if (!ownsCurrentRequest()) {
      clearStaticMapPreviewState();
      onError?.();
    } else if (root.dataset[STATIC_MAP_PREVIEW_LOADED_DATASET_KEY] === 'true') {
      handleLoad();
    } else if (root.dataset[STATIC_MAP_PREVIEW_ERROR_DATASET_KEY] === 'true') {
      handleError();
    } else {
      timeoutHandle = window.setTimeout(handleError, STATIC_MAP_PREVIEW_REQUEST_TIMEOUT_MS);
    }

    return () => {
      clearTimeoutHandle();
      window.removeEventListener(STATIC_MAP_PREVIEW_LOAD_EVENT, handleLoad);
      window.removeEventListener(STATIC_MAP_PREVIEW_ERROR_EVENT, handleError);
      if (ownsCurrentRequest()) clearStaticMapPreviewState();
    };
  }, [onError, onLoad, requestPrefix]);

  return (
    <>
      <div
        className={previewImage}
        style={{
          backgroundImage: 'var(--hotpads-static-map-preview-image, none)',
          backgroundSize: '100% 100%',
        }}
        data-static-map-preview
      />
      <script
        data-static-map-preview-bootstrap
        dangerouslySetInnerHTML={{ __html: buildStaticMapPreviewBootstrapScript(bounds) }}
      />
    </>
  );
}
